import { createSlice } from '@reduxjs/toolkit';

const TOKEN_KEY = 'commissioner.accessToken';
const USER_KEY = 'commissioner.user';

// Hydrate from localStorage so a page refresh keeps the user logged in.
// Note: localStorage is OK for a SaaS dev build but should move to httpOnly
// cookies before production — XSS protection.
const loadInitial = () => {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const userJson = localStorage.getItem(USER_KEY);
        if (token && userJson) {
            return { accessToken: token, user: JSON.parse(userJson) };
        }
    } catch {
        // Ignore — fall through to logged-out state.
    }
    return { accessToken: null, user: null };
};

const initialState = {
    ...loadInitial(),
    status: 'idle',  // 'idle' | 'loading' | 'failed'
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginStart(state) {
            state.status = 'loading';
            state.error = null;
        },
        loginSuccess(state, action) {
            const { accessToken, ...user } = action.payload;
            state.accessToken = accessToken;
            state.user = user;
            state.status = 'idle';
            state.error = null;
            try {
                localStorage.setItem(TOKEN_KEY, accessToken);
                localStorage.setItem(USER_KEY, JSON.stringify(user));
            } catch {
                /* localStorage disabled — auth still works for this session */
            }
        },
        loginFailure(state, action) {
            state.status = 'failed';
            state.error = action.payload;
            state.accessToken = null;
            state.user = null;
        },
        logout(state) {
            state.accessToken = null;
            state.user = null;
            state.status = 'idle';
            state.error = null;
            try {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
            } catch {
                /* ignore */
            }
        },
    },
});

export const { loginStart, loginSuccess, loginFailure, logout } = authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state) => Boolean(state.auth.accessToken);
export const selectUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
